/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { Effect, FullAssignedTechnicianEntry, Nullable } from "../../utils/Interfaces";

import React, { useState, useEffect } from "react";
import { Typography, Box, Container, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";

import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { updateTechnicianEntry, updateTechnicianWorkOrderHistory } from "./technicianWorkOrderFunctions";
import TechnicianWorkOrderHeader from "./TechnicianWorkOrderHeader";
import TechnicianWorkOrderHistory from "./TechnicianWorkOrderHistory";
import { useWindowDimensions } from "../../utils/Hooks";
import { useAppDispatch } from "../../app/hooks";
import TechnicianDashboardEntryV2 from "./TechnicianDashboardEntryV2";

const TechnicianWorkOrder = () => {
    const { entryId } = useParams();
    const dispatch = useAppDispatch();

    const { width } = useWindowDimensions();
    let mobile: boolean = width <= 600;

    const [entry, setEntry] = useState<Nullable<FullAssignedTechnicianEntry>>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [history, setHistory] = useState<FullAssignedTechnicianEntry[]>([]);
    const [historyLoaded, setHistoryLoaded] = useState<boolean>(false);

    const [ticketErrorMessage, setTicketErrorMessage] = useState<string>("");

    function refreshEntry(loadingAnimation: boolean = true, action: Effect) {
        updateTechnicianEntry(
            parseInt(entryId as string),
            dispatch,
            (techEntry) => {
                setEntry(techEntry);
                // Only fetch history on the first pass.
                if (!historyLoaded) {
                    setHistoryLoaded(true);
                    updateTechnicianWorkOrderHistory(
                        techEntry.work_order.id,
                        techEntry.trip_number,
                        dispatch,
                        setHistory,
                        loadingAnimation ? setLoading : (loading) => {}
                    );
                }
                action();
            },
            setTicketErrorMessage,
            loadingAnimation ? setLoading : (loading) => {}
        );
    }

    useEffect(() => refreshEntry(true, () => {}), [entryId]);

    if (loading) {
        return (
            <Box style={{ padding: 0 }}>
                <StyledBreadcrumbs paths={[{ text: "Techwise Portal", route: "/" }, null, null]} />
            </Box>
        );
    }

    if (!entry) {
        return (
            <Box style={{ padding: 0 }}>
                <StyledBreadcrumbs
                    paths={[
                        { text: "Techwise Portal", route: "/" },
                        { text: mobile ? "Services" : "Service Dashboard", route: "/tech-dash?status=IP" },
                        { text: "Invalid Trip", route: `/tech-dash/${entryId}` },
                    ]}
                />

                {ticketErrorMessage !== "" && (
                    <Box width={"100%"} display={"flex"} flexDirection={"row"} justifyContent={"center"}>
                        <Typography variant="h5">{ticketErrorMessage}</Typography>
                    </Box>
                )}

                {ticketErrorMessage === "" && (
                    <Box width={"100%"} display={"flex"} flexDirection={"row"} justifyContent={"center"} marginTop={"50%"}>
                        <CircularProgress size={"4rem"} />
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box style={{ padding: 0 }}>
            <Container style={{ padding: 0 }}>
                <StyledBreadcrumbs
                    paths={[
                        { text: "Techwise Portal", route: "/" },
                        {
                            text: mobile ? "Services" : "Service Dashboard",
                            route: `/tech-dash?status=${entry.submitted ? "SB" : "IP"}`,
                        },
                        { text: `#${entry.work_order.ticket_no}`, route: `/tech-dash/${entryId}` },
                    ]}
                />
                <Box marginBottom={"15px"} />
                <TechnicianWorkOrderHeader
                    workOrder={entry.work_order}
                    tasks={entry.work_order_tasks}
                    refreshEntry={() => refreshEntry(false, () => {})}
                />

                {history.length > 0 && <TechnicianWorkOrderHistory history={history} />}

                <TechnicianDashboardEntryV2
                    technicianEntry={entry}
                    refreshEntry={(action: Effect) => refreshEntry(false, action)}
                />
            </Container>
        </Box>
    );
};

export default TechnicianWorkOrder;
